/**
 * TakeLogStream — the Horizontal Note Timeline (DESIGN_SPEC §4.3).
 *
 * Notes flow left to right, oldest first, each drawn as a card with its
 * speaker on it, with a timeline ruler beneath and the input hanging off
 * the right edge. `DESIGN_SPEC.md` §9 names the reason in the negative
 * twice: a vertical chat transcript on the Stage is the shape every LLM
 * demo already has, and animachord's defining capability is *state over
 * time across multiple voices*, so the time axis has to be the dominant
 * axis of the screen rather than a scrollback.
 *
 * ## What the direction loop decided (animachord#55)
 *
 * Three treatments were rendered in the real stage frame with a
 * forty-note fixture -- a rupture, an apology, three speakers -- and
 * read at 1440x900. Evidence and the two rejected renders:
 * `docs/evidence/takelog-2026-09-05/`.
 *
 * - **A, ledger strip** (this one): one lane, a ruler tick under every
 *   card, the input pinned outside the scroller.
 * - **B, staves**: one lane per voice on a shared x-axis. It shows who
 *   speaks better than anything else tried and costs too much to keep --
 *   four notes on screen at 1440 against A's seven, because three lanes
 *   of card height is the whole viewport. It is also the only one where
 *   most of the surface is empty lane.
 * - **C, cascade**: one lane, gaps proportional to elapsed silence,
 *   crowded cards staggered onto a second row. The stagger broke reading
 *   order -- a card below and left of another one is read as earlier and
 *   is not -- which is exactly the property this component owes a screen
 *   reader, and the proportional gaps read as missing content rather
 *   than as silence.
 *
 * A won on the property the other two lost: **one lane, strictly left to
 * right, is the same order on screen, in the DOM, and to a screen
 * reader.** It also keeps the input reachable at every scroll position,
 * where B and C both put it somewhere the reader has to travel to.
 *
 * Three things the renders showed that are fixed here rather than
 * carried: the ruler is structurally coupled to the cards (each note and
 * its tick are one column, so they cannot drift apart the way they did
 * in the spike); a leader hairline runs from each card down to its own
 * tick, so a short card is still tied to the axis; and crowding is
 * marked on the ruler as a delta rather than by moving the card, because
 * moving the card is what broke C.
 *
 * ## Attribution
 *
 * Every note names who said it. That was free while the room held one
 * person -- `kind` alone told you whether a line was the character or
 * you -- and it stops being free the moment a visitor puts someone else
 * in the room: two characters are both `kind: "agent"` and rendered
 * identically, so the log became a conversation with no way to tell who
 * was in it.
 *
 * The name is the carrier, not the colour. Colour as the sole signal
 * would fail WCAG on its own, and it would also need a per-voice hue for
 * each character, which means inventing tokens outside the palette for a
 * question text answers better. All voices stay in brass; the name on
 * the card says which one. This layer is unchanged from #12 -- it was
 * written in the spec's own per-note vocabulary so it would survive the
 * axis changing, and it did.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { EmptyReplyReason } from "@/api/types";

import { takeLogStreamCopy } from "./TakeLogStream.copy";

/**
 * A wall-clock time from whatever the caller passed.
 *
 * The socket stamps frames with a full ISO instant, and the log was
 * rendering it verbatim: microsecond precision and a UTC offset, in a
 * surface whose brand lock is *"legible at a glance to someone who has
 * never seen it"*. Nobody watching a rehearsal needs the microsecond, and
 * on a 13-inch laptop it cost the character her voice -- 240px of the row
 * spent on a debug artifact, leaving 15px for what she said.
 *
 * Seconds are kept because a rehearsal log is about timing; anything
 * finer is instrumentation. An unparseable string is returned as-is
 * rather than blanked, so a caller passing an already-short label like
 * "19:04" still reads correctly, and the bounded card upstream stops any
 * such string eating the note regardless.
 */
export function formatLogTime(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return timestamp;
  return parsed.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * Seconds from the first note, as the ruler prints them.
 *
 * `t=` prefixed and one decimal, which is §4.3's own notation. Returns
 * `null` when either stamp is unparseable rather than printing `t=NaNs`:
 * a ruler that has lost its origin should show a bare tick, because a
 * tick with no number still says "a note happened here" and `NaN` says
 * the component is broken.
 */
export function elapsedLabel(timestamp: string, origin: string): string | null {
  const t = Date.parse(timestamp);
  const t0 = Date.parse(origin);
  if (Number.isNaN(t) || Number.isNaN(t0)) return null;
  return `t=${((t - t0) / 1000).toFixed(1)}s`;
}

/**
 * How close two notes have to be before the ruler calls them a crowd.
 *
 * Two seconds, because that is the gap the fixture's two characters
 * answer each other in and it is the case the direction loop was
 * comparing. Below it, printing both absolute stamps puts two nearly
 * identical numbers side by side and the reader has to subtract them to
 * learn the only thing that is interesting, which is that the second
 * voice came in on top of the first.
 */
export const CROWD_SECONDS = 2;

export type LogLineKind = "agent" | "human" | "system" | "cue";

export interface LogLine {
  readonly id: string;
  readonly kind: LogLineKind;
  readonly timestamp: string;
  readonly text: string;
  /**
   * Who said it. Optional so a note with no speaker (a system note, a
   * cue) renders without one rather than inventing a name; `kind`
   * already carries what those notes are.
   */
  readonly speaker?: string;
  /**
   * Streaming identity, for consumers that must reassemble a turn.
   *
   * A character's reply arrives as several `server.note` frames: deltas
   * with `partial: true` sharing a `parent_note_id`, then one complete
   * frame whose id *is* that parent. The take log renders every frame,
   * which is right for a log. A conversation must not, so it needs these
   * to group them; this component ignores both.
   */
  readonly partial?: boolean;
  readonly parentId?: string;
  /**
   * Why this note carries no text, when it carries none (#153).
   *
   * Rendered as a reason **only when `text` is empty**, so a frame that
   * arrives marked and full still shows what was said. The precedence is
   * the same one `rehearsal/emptyReply.ts` states for the visitor
   * surface, for the same reason: a one-word reply is terse, not blank,
   * and a log that annotated it would be lying about the best turns.
   */
  readonly empty?: EmptyReplyReason;
}

export interface TakeLogStreamProps {
  readonly lines: readonly LogLine[];
  /** Keep the newest note in view as notes arrive. */
  readonly autoScroll?: boolean;
  readonly maxLines?: number;
  /**
   * What hangs off the right edge (DESIGN_SPEC §4.3).
   *
   * A slot rather than the field itself, because the field is wired to
   * the socket and this component fetches nothing. The Stage passes its
   * own note form in, which is how the input can sit at the head of the
   * timeline without this component learning what sending a note means.
   */
  readonly input?: ReactNode;
}

const KIND_COLOR: Record<LogLineKind, string> = {
  agent: "var(--brass-cue)",
  human: "var(--paper-cream)",
  system: "var(--paper-cream-dim)",
  cue: "var(--brass-strike)",
};

/**
 * `◯` for the visitor and `●` for a voice, which is §4.3's own drawing.
 * The filled mark is someone the stage is running; the open one is you.
 */
const KIND_GLYPH: Record<LogLineKind, string> = {
  agent: "●",
  human: "◯",
  system: "·",
  cue: "✦",
};

const NOTE_MODIFIER: Record<LogLineKind, string> = {
  agent: "take-log__note--voice",
  human: "take-log__note--user",
  system: "take-log__note--system",
  cue: "take-log__note--cue",
};

/**
 * What to print in the card body, which is not always `line.text`.
 *
 * Text first, always. `empty` is only consulted once there is nothing to
 * show, so a marked frame that somehow carries words prints the words —
 * the same precedence `treatmentFor` applies on the visitor surface, and
 * the reason it is a function here rather than a condition inline is
 * that inline is where it would be got wrong the next time a branch is
 * added below.
 *
 * An unrecognised reason prints the raw value rather than nothing. A log
 * whose reader is a developer should say "empty: some_new_reason" rather
 * than silently render the blank card this exists to remove.
 */
export function logTextFor(line: LogLine): string {
  if (line.text.length > 0) return line.text;
  if (line.empty === undefined) return "";
  const known: Record<string, string> = takeLogStreamCopy.emptyReason;
  return known[line.empty] ?? `empty: ${line.empty}`;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  // Optional all the way down. This is called from an effect that runs on
  // every arriving note, so a `matchMedia` that returns nothing -- an
  // incomplete test double, an embedder's shim -- takes the whole stage
  // down rather than losing one preference. `false` is the safe default:
  // it means "animate", and the global reduced-motion block in
  // `index.css` still forces `auto` for a reader who asked for it.
  return window.matchMedia("(prefers-reduced-motion: reduce)")?.matches ?? false;
}

/**
 * The scroll positions a note can snap to, in document order.
 *
 * Read off the DOM rather than computed from widths, because the widths
 * are content-driven: §4.3 makes a note 160px to 320px depending on what
 * was said, so there is no stride to multiply. `offsetLeft` is measured
 * against the lane, and the lane's own padding is subtracted so the
 * first note snaps to zero rather than to its inset.
 */
export function snapPositions(lane: HTMLElement | null): number[] {
  if (!lane) return [];
  const base = (lane.firstElementChild as HTMLElement | null)?.offsetLeft ?? 0;
  return Array.from(lane.children).map(
    (child) => (child as HTMLElement).offsetLeft - base,
  );
}

/**
 * The snap position to move to from where the strip is now.
 *
 * Exported and pure because the arithmetic is the behaviour worth
 * testing, and testing it through a scroll event in a DOM with no layout
 * engine would be testing the mock. `epsilon` absorbs the sub-pixel
 * difference between a scroll that has settled on a snap point and one
 * that is a rounding error away from it; without it "next" from a
 * settled position returns that same position and the strip stops
 * moving.
 */
export function nextSnap(
  positions: readonly number[],
  current: number,
  direction: 1 | -1,
  epsilon = 2,
): number | null {
  if (positions.length === 0) return null;
  if (direction === 1) {
    const found = positions.find((p) => p > current + epsilon);
    return found ?? (positions[positions.length - 1] as number);
  }
  const earlier = positions.filter((p) => p < current - epsilon);
  return earlier.length > 0
    ? (earlier[earlier.length - 1] as number)
    : (positions[0] as number);
}

export function TakeLogStream({
  lines,
  autoScroll = true,
  maxLines = 200,
  input,
}: TakeLogStreamProps): React.ReactElement {
  const [enabled, setEnabled] = useState<boolean>(autoScroll);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const laneRef = useRef<HTMLOListElement>(null);
  const visibleLines = lines.slice(-maxLines);

  /**
   * The ruler's origin is the oldest note *still rendered*, not the
   * oldest note that ever arrived. Past `maxLines` the two differ, and a
   * ruler counting from a note that is no longer on the strip prints an
   * origin the reader cannot see.
   */
  const origin = visibleLines[0]?.timestamp;

  const ticks = useMemo(
    () =>
      visibleLines.map((line, i) => {
        const previous = visibleLines[i - 1];
        const gap =
          previous === undefined
            ? null
            : (Date.parse(line.timestamp) - Date.parse(previous.timestamp)) / 1000;
        const crowded = gap !== null && Number.isFinite(gap) && gap < CROWD_SECONDS;
        return {
          id: line.id,
          crowded,
          label: crowded
            ? `+${(gap as number).toFixed(1)}s`
            : origin === undefined
              ? null
              : elapsedLabel(line.timestamp, origin),
        };
      }),
    [visibleLines, origin],
  );

  const scrollTo = useCallback((left: number): void => {
    const el = scrollerRef.current;
    if (!el) return;
    const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
    el.scrollTo({ left, behavior });
  }, []);

  /**
   * Newest note in view. Right edge, because the axis is left-to-right.
   *
   * Measured in a frame rather than in the effect body, and that is not a
   * precaution: at 393px the strip is fifty viewports wide, and reading
   * `scrollWidth` before the browser had laid the cards out gave a target
   * short of the end, so the newest note was off screen on a phone while
   * being correctly in view on a laptop. One frame later the number is
   * the real one. Cancelled on cleanup so a burst of arriving notes
   * cannot leave a frame pending against a scroller that has gone.
   */
  useEffect(() => {
    if (!enabled) return;
    const el = scrollerRef.current;
    if (!el) return;
    const frame = requestAnimationFrame(() => {
      const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
      el.scrollTo({ left: el.scrollWidth, behavior });
    });
    return () => cancelAnimationFrame(frame);
  }, [enabled, visibleLines.length]);

  /**
   * A vertical wheel moves the strip sideways.
   *
   * A mouse wheel emits `deltaY` and nothing else, so without this the
   * one input most desktop readers have does nothing at all on the one
   * surface that is horizontal. Registered natively rather than through
   * `onWheel` because React's wheel listener is passive and
   * `preventDefault` inside it is a no-op with a console warning.
   *
   * A wheel over a card whose own body is still scrollable is left
   * alone, so a long note can be read without the strip travelling out
   * from under it.
   */
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent): void => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const body = (event.target as HTMLElement | null)?.closest?.(
        "[data-take-log-body]",
      ) as HTMLElement | null;
      if (body && body.scrollHeight - body.clientHeight > 1) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const el = scrollerRef.current;
      if (!el) return;
      const positions = snapPositions(laneRef.current);
      if (positions.length === 0) return;

      let target: number | null = null;
      if (event.key === "ArrowRight") target = nextSnap(positions, el.scrollLeft, 1);
      else if (event.key === "ArrowLeft") target = nextSnap(positions, el.scrollLeft, -1);
      else if (event.key === "Home") target = positions[0] as number;
      else if (event.key === "End") target = positions[positions.length - 1] as number;
      if (target === null) return;

      event.preventDefault();
      /*
        Moving by hand means you no longer want the strip moving on its
        own; a reader who has walked back to the rupture and is pulled to
        the newest note by the next frame has lost their place. Arrowing
        to the last note does not re-arm it, because re-arming on arrival
        makes the toggle a thing that turns itself back on.
      */
      setEnabled(false);
      scrollTo(target);
    },
    [scrollTo],
  );

  return (
    <div
      data-testid="take-log-stream"
      role="log"
      aria-label={takeLogStreamCopy.ariaLabel}
      aria-live="polite"
      className="take-log bg-rehearsal-taupe border-ledger-line rounded-ledger-line"
      // `flex: 1` and `width: 100%`, not just `height: 100%`. The stage
      // row is a flex container, so without a grow the strip sizes to its
      // content -- which with an empty log is the width of "no log lines
      // yet". The first frame of the demo then showed a narrow box beside
      // a full-width score, which reads as broken rather than as empty.
      style={{
        display: "flex",
        flex: 1,
        width: "100%",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "0.75rem",
          padding: "0.25rem 0.5rem",
          borderBottom: "1px solid var(--measure-line)",
        }}
      >
        <span
          className="font-plex-mono"
          style={{
            color: "var(--paper-cream-faint)",
            fontSize: "0.656rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {takeLogStreamCopy.scrollHint}
        </span>
        <button
          type="button"
          data-testid="take-log-autoscroll-toggle"
          aria-pressed={enabled}
          onClick={(): void => setEnabled((v) => !v)}
          className="font-plex-mono text-paper-cream-dim"
          style={{
            marginLeft: "auto",
            background: "transparent",
            border: 0,
            fontSize: "0.781rem",
            padding: "0.25rem 0.5rem",
          }}
        >
          {enabled ? takeLogStreamCopy.scrollToggleOn : takeLogStreamCopy.scrollToggleOff}
        </button>
      </div>

      <div
        className="take-log__body"
        style={{ display: "flex", alignItems: "stretch", minWidth: 0 }}
      >
        <div
          ref={scrollerRef}
          data-testid="take-log-scroll"
          className="take-log__container"
          tabIndex={0}
          aria-label={takeLogStreamCopy.rulerLabel}
          onKeyDown={onKeyDown}
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            // Asserted in tests on the inline style rather than on the
            // stylesheet: happy-dom has no cascade for a class rule, so a
            // test reading the computed style would pass whatever the CSS
            // said. §6.5 puts the same declarations in `index.css` for the
            // momentum and scrollbar treatment.
            scrollSnapType: "x mandatory",
          }}
        >
          {visibleLines.length === 0 ? (
            <div
              data-testid="take-log-empty"
              style={{ padding: "1.25rem 0.75rem 0.5rem", minWidth: "12rem" }}
            >
              <p
                className="text-paper-cream-dim font-plex-mono"
                style={{ margin: "0 0 1rem", fontSize: "0.781rem" }}
              >
                {takeLogStreamCopy.emptyLabel}
              </p>
              {/* The ruler is drawn empty rather than omitted: a stage
                  before its first note is ready, not broken. */}
              <div
                className="take-log__ruler"
                style={{
                  borderTop: "1px solid var(--measure-line)",
                  paddingTop: "0.3rem",
                }}
              >
                <span
                  className="font-plex-mono"
                  style={{ fontSize: "0.656rem", color: "var(--paper-cream-faint)" }}
                >
                  {takeLogStreamCopy.originLabel}
                </span>
              </div>
            </div>
          ) : (
            <ol
              ref={laneRef}
              dir="ltr"
              data-testid="take-log-lane"
              /*
                An ordered list, and the order is the chronology. Screen
                reader order, DOM order and screen order are one sequence
                and stay that way: nothing here reorders, and the ruler
                cell for a note lives inside that note's own list item, so
                no future column arrangement can separate them either.
              */
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: "0.75rem",
                listStyle: "none",
                margin: 0,
                padding: "0.75rem 0.75rem 0.5rem",
              }}
            >
              {visibleLines.map((line, i) => {
                const tick = ticks[i];
                return (
                  <li
                    key={line.id}
                    data-testid={`log-line-${line.id}`}
                    data-kind={line.kind}
                    data-speaker={line.speaker ?? undefined}
                    data-crowded={tick?.crowded ? "true" : undefined}
                    className={`take-log__note ${NOTE_MODIFIER[line.kind]}`}
                    style={{
                      // §4.3: min 160px, max 320px, content-driven. The
                      // floor is the same guarantee the vertical log's
                      // 10rem text floor was -- a note can never be
                      // squeezed to a column of one word per line -- and
                      // here it is the whole card, so no future column
                      // arrangement can take it back.
                      minWidth: "10rem",
                      maxWidth: "20rem",
                      flexShrink: 0,
                      scrollSnapAlign: "start",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <article
                      style={{
                        background: "var(--stage-riser)",
                        border: "1px solid var(--measure-line)",
                        borderTop: `2px solid ${KIND_COLOR[line.kind]}`,
                        borderRadius: "var(--radius)",
                        padding: "0.5rem 0.6rem",
                        fontFamily: "var(--font-plex-mono)",
                        fontSize: "0.781rem",
                        lineHeight: 1.5,
                      }}
                    >
                      <header
                        style={{
                          display: "flex",
                          gap: "0.4rem",
                          alignItems: "baseline",
                          marginBottom: "0.35rem",
                          fontSize: "0.656rem",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: KIND_COLOR[line.kind],
                        }}
                      >
                        <span aria-hidden>{KIND_GLYPH[line.kind]}</span>
                        {/* Fixed-width so a long name cannot push the
                            time off the card, and ellipsised rather than
                            wrapped so the header stays one line. */}
                        <span
                          data-testid={`log-speaker-${line.id}`}
                          title={line.speaker ?? undefined}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {line.speaker ?? ""}
                        </span>
                        {/* `--paper-cream-faint`, which clears 4.5:1 on
                            --stage-riser (`tests/token-contrast.test.ts`
                            computes it), and bounded at 8ch. This span
                            rendered `line.timestamp` raw once, and on the
                            live page that is a full ISO instant --
                            thirty-two monospace characters that neither
                            wrap nor shrink, measured at 240px. The width
                            is the fix for the class rather than for
                            today's format: a provider that starts sending
                            something longer cannot take the card back. */}
                        <span
                          data-testid={`log-time-${line.id}`}
                          title={line.timestamp}
                          style={{
                            width: "8ch",
                            flexShrink: 0,
                            textAlign: "right",
                            overflow: "hidden",
                            color: "var(--paper-cream-faint)",
                            /*
                              `ch` measures the font's advance and knows
                              nothing about tracking, so the header's
                              0.06em on eight characters put 55px of
                              "12:04:00" in a 50px box and clipped the
                              seconds off every card on the stage.
                              Measured, not reasoned: `scrollWidth` 55
                              against `clientWidth` 50 at 1440x900.
                              Tracking is for the uppercase labels beside
                              this; a time is a number and reads worse
                              spaced out anyway.
                            */
                            letterSpacing: "0px",
                          }}
                        >
                          {formatLogTime(line.timestamp)}
                        </span>
                      </header>
                      <p
                        data-testid={`log-text-${line.id}`}
                        data-take-log-body
                        style={{
                          margin: 0,
                          color: KIND_COLOR[line.kind],
                          // Take ids and session ids are long unbroken
                          // tokens and will otherwise run under the card's
                          // edge and clip, which reads as a rendering
                          // fault rather than as a long id.
                          overflowWrap: "anywhere",
                          // One pathological note cannot set the height of
                          // the whole strip. The text stays reachable
                          // rather than being clipped away, and the wheel
                          // handler leaves a card alone while its body can
                          // still scroll.
                          maxHeight: "16rem",
                          overflowY: "auto",
                          // A reason is the log describing the frame
                          // rather than quoting it, so it is set apart the
                          // way a log has always set apart its own
                          // commentary.
                          fontStyle:
                            line.text.length === 0 && line.empty !== undefined
                              ? "italic"
                              : undefined,
                        }}
                      >
                        {logTextFor(line)}
                      </p>
                    </article>

                    {/*
                      The leader, and it is the fix for the thing the
                      direction loop found. With the ruler a fixed
                      distance below a lane of ragged-bottomed cards, a
                      short card floated with nothing tying it to its own
                      tick, and the eye read the tick as belonging to
                      whichever card was tallest nearby. A hairline from
                      the card down to the tick is ledger vocabulary and
                      it makes the association visible rather than
                      inferred.
                    */}
                    <span
                      aria-hidden
                      style={{
                        flex: 1,
                        minHeight: "0.75rem",
                        width: 1,
                        marginLeft: "0.6rem",
                        background: "var(--ledger-line)",
                      }}
                    />

                    <div
                      className="take-log__ruler"
                      data-testid={`log-tick-${line.id}`}
                      style={{
                        borderTop: `1px solid ${
                          tick?.crowded ? "var(--brass-tarnish)" : "var(--measure-line)"
                        }`,
                        paddingTop: "0.3rem",
                        fontFamily: "var(--font-plex-mono)",
                        fontSize: "0.656rem",
                        color: "var(--paper-cream-faint)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {/* The tick itself: a measure line rising into the
                          ruler at the note's own t-stamp. */}
                      <span
                        aria-hidden
                        style={{
                          display: "block",
                          width: 1,
                          height: "0.3rem",
                          marginBottom: "0.15rem",
                          background: "var(--barline-strong)",
                        }}
                      />
                      {tick?.label ?? ""}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/*
          The head of the timeline. Off the right edge and outside the
          scroller, so it is reachable at every scroll position: a reader
          who has walked back to the rupture can still type without
          travelling to the end first, which is the thing directions B and
          C both gave up.
        */}
        {input === undefined ? null : (
          <div
            className="take-log__cursor"
            data-testid="take-log-cursor"
            style={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "0.4rem",
              padding: "0.75rem",
              borderLeft: "1px solid var(--measure-line)",
              background: "var(--stage-pit)",
            }}
          >
            <span
              className="font-plex-mono"
              style={{
                fontSize: "0.656rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--brass-cue)",
              }}
            >
              ▶ {takeLogStreamCopy.nowLabel}
            </span>
            {input}
          </div>
        )}
      </div>
    </div>
  );
}

export default TakeLogStream;
